import { formatDateRange, formatPackagePriceLabel } from '@longy/common'
import { EVENT_PAGE_SECTIONS } from '@longy/common/cms'
import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Calendar, ChevronLeft, MapPin, Users } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachedBlocks, useAttachedBlocks } from '../../components/cms/AttachedBlocks'
import { EarlyBirdPrice } from '../../components/events/EarlyBirdPrice'
import { ImageCarousel } from '../../components/events/ImageCarousel'
import { WebinarBadge } from '../../components/events/WebinarBadge'
import { Footer, Header } from '../../components/landing'
import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { deriveEventBookingState } from '../../utils/eventBooking'
import { priceFormatterFor } from '../../utils/formatPrice'
import { buildOgMeta } from '../../utils/og'
import { longFormRichTextClassName } from '../../utils/richText'
import { withoutCmsBlocks } from '../../utils/withoutCmsBlocks'

const bookingButtonClassName =
    'inline-block rounded-full bg-primary px-8 py-3 font-bold text-white transition-colors hover:bg-primary/90'

const PACKAGE_SELECT_INPUT_ID = 'event-package-select-input'

const EventDetailsPage = () => {
    const { eventId } = Route.useParams()
    const { getLocalizedValue } = useLocale()
    const { t } = useTranslation()
    const [selectedPackageId, setSelectedPackageId] = useState('')
    const [promptRequested, setPromptRequested] = useState(false)
    const packageSelectRef = useRef<HTMLSelectElement>(null)

    const { data: event, isLoading } = api.event.getById.useQuery({ id: eventId })
    const hasAttachedBlocks = useAttachedBlocks(event?.cmsBlocks).length > 0
    const longDescriptionHtml = event?.longDescription
        ? getLocalizedValue(event.longDescription)
        : ''

    const packages = event?.packages ?? []
    const formatEventPrice = priceFormatterFor(
        event?.tax,
        (price) => t('app.event.netPlusVat', { price }),
        t('common.ui.freeOfCharge'),
    )
    const {
        isPast,
        isFull,
        canBook,
        hasMultiplePackages,
        needsPackageSelection,
        showPackagePrompt,
        displayedPrice,
        displayedOriginalPrice,
        displayedEarlyBirdDeadline,
        showFromSuffix,
        checkoutHref,
    } = deriveEventBookingState({
        eventId,
        packages,
        selectedPackageId,
        startDate: event?.startDate,
        minPrice: event?.minPrice,
        minOriginalPrice: event?.minOriginalPrice,
        earlyBirdDeadline: event?.earlyBirdDeadline,
        now: new Date(),
        promptRequested,
        fullyBooked: event?.fullyBooked ?? false,
    })

    const handlePackagePrompt = () => {
        setPromptRequested(true)
        packageSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        packageSelectRef.current?.focus({ preventScroll: true })
    }

    const renderBookingButton = () => {
        if (!canBook) {
            return (
                <span
                    aria-disabled="true"
                    className="inline-block cursor-not-allowed rounded-full bg-primary/40 px-8 py-3 font-bold text-white"
                >
                    {isFull ? t('frontend.events.fullyBooked') : 'Jelentkezés'}
                </span>
            )
        }

        if (needsPackageSelection) {
            return (
                <button
                    type="button"
                    className={bookingButtonClassName}
                    onClick={handlePackagePrompt}
                >
                    Jelentkezés
                </button>
            )
        }

        return (
            <a href={checkoutHref} className={bookingButtonClassName}>
                Jelentkezés
            </a>
        )
    }

    return (
        <div className="min-h-screen bg-off-white font-sans text-brand-dark">
            <Header />
            <main className="flex flex-col">
                <section className="bg-white py-12">
                    <div className="mx-auto w-full max-w-[960px] px-6">
                        <Link
                            className="mb-8 inline-flex items-center gap-1 text-sm font-medium text-brand-gray transition-colors hover:text-primary"
                            to="/events"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Vissza az eseményekhez
                        </Link>

                        {isLoading ? (
                            <div>
                                <Skeleton className="mb-8 aspect-[2/1] w-full rounded-2xl" />
                                <Skeleton className="mb-4 h-8 w-64" />
                                <div className="mb-8 space-y-3">
                                    <Skeleton className="h-5 w-48" />
                                    <Skeleton className="h-5 w-40" />
                                    <Skeleton className="h-5 w-36" />
                                </div>
                                <Skeleton className="h-40 w-full" />
                            </div>
                        ) : !event ? (
                            <div className="py-24 text-center">
                                <p className="text-lg text-brand-gray">Az esemény nem található.</p>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-8">
                                    <ImageCarousel
                                        alt={event.name ? getLocalizedValue(event.name) : 'Esemény'}
                                        images={event.images ?? []}
                                    />
                                </div>

                                <WebinarBadge className="mb-4 inline-block" type={event.type} />

                                <h1 className="mb-6 text-3xl font-bold text-brand-dark md:text-4xl">
                                    {event.name ? getLocalizedValue(event.name) : 'Esemény'}
                                </h1>

                                <div className="mb-8 flex flex-wrap gap-6 text-sm text-brand-gray">
                                    {event.startDate && (
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-5 w-5 text-primary" />
                                            <span>
                                                {formatDateRange(event.startDate, event.endDate)}
                                            </span>
                                        </div>
                                    )}
                                    {event.locationName &&
                                        getLocalizedValue(event.locationName) && (
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-5 w-5 text-primary" />
                                                <span>{getLocalizedValue(event.locationName)}</span>
                                            </div>
                                        )}
                                    <div className="flex items-center gap-2">
                                        <Users className="h-5 w-5 text-primary" />
                                        <span>
                                            {isFull
                                                ? t('frontend.events.fullyBooked')
                                                : `${t('frontend.events.freePlaces')}: ${event.availableSpaces} / ${event.maxParticipants}`}
                                        </span>
                                    </div>
                                </div>

                                {event.categoryName && (
                                    <div className="mb-8">
                                        <span className="rounded-full bg-brand-yellow px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-dark">
                                            {getLocalizedValue(event.categoryName)}
                                        </span>
                                    </div>
                                )}

                                {isPast && (
                                    <div className="mb-8 rounded-2xl bg-slate-100 px-6 py-4 text-sm font-semibold text-brand-gray">
                                        Ez az esemény már lezárult.
                                    </div>
                                )}

                                {!isPast && isFull && (
                                    <div className="mb-8 rounded-2xl bg-slate-100 px-6 py-4 text-sm font-semibold text-brand-gray">
                                        {t('frontend.events.fullyBookedNotice')}
                                    </div>
                                )}

                                <div
                                    id={EVENT_PAGE_SECTIONS.booking}
                                    className="mb-8 flex scroll-mt-24 flex-wrap items-center gap-4"
                                >
                                    {renderBookingButton()}
                                    {displayedPrice !== null && (
                                        <EarlyBirdPrice
                                            deadline={displayedEarlyBirdDeadline}
                                            format={formatEventPrice}
                                            originalPrice={displayedOriginalPrice}
                                            price={displayedPrice}
                                            suffix={showFromSuffix ? '-tól' : undefined}
                                        />
                                    )}
                                </div>

                                {/* The anchor wraps the selector instead of being the selector: the
                                    admin offers this CTA target for every event, and on a
                                    single-package one there is nothing to choose, so the link has
                                    to land on the booking area rather than nowhere. */}
                                <div id={EVENT_PAGE_SECTIONS.packages} className="scroll-mt-24">
                                    {hasMultiplePackages && (
                                        <div className="mb-8">
                                            <label
                                                htmlFor={PACKAGE_SELECT_INPUT_ID}
                                                className="mb-2 block text-sm font-semibold text-brand-dark"
                                            >
                                                Válassz csomagot!
                                            </label>
                                            <select
                                                ref={packageSelectRef}
                                                id={PACKAGE_SELECT_INPUT_ID}
                                                value={selectedPackageId}
                                                aria-invalid={showPackagePrompt}
                                                aria-describedby={
                                                    showPackagePrompt
                                                        ? 'event-package-select-error'
                                                        : undefined
                                                }
                                                className={`w-full max-w-md appearance-none rounded-2xl border bg-white px-6 py-4 text-brand-dark shadow-sm transition-all focus:border-transparent focus:outline-none focus:ring-2 ${
                                                    showPackagePrompt
                                                        ? 'border-red-500 focus:ring-red-500'
                                                        : 'border-slate-200 focus:ring-primary'
                                                }`}
                                                onChange={(e) =>
                                                    setSelectedPackageId(e.target.value)
                                                }
                                            >
                                                <option value="">Válassz csomagot...</option>
                                                {packages.map((pkg) => (
                                                    <option key={pkg.id} value={pkg.id}>
                                                        {pkg.name} –{' '}
                                                        {formatPackagePriceLabel(
                                                            pkg,
                                                            formatEventPrice,
                                                            t(
                                                                'frontend.events.earlyBirdOptionSuffix',
                                                            ),
                                                        )}
                                                    </option>
                                                ))}
                                            </select>
                                            {showPackagePrompt && (
                                                <p
                                                    id="event-package-select-error"
                                                    className="mt-2 text-sm font-medium text-red-500"
                                                >
                                                    {t('app.event.selectTicketTypeToContinue')}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {(hasAttachedBlocks || longDescriptionHtml) && (
                                    <div
                                        id={EVENT_PAGE_SECTIONS.description}
                                        className="scroll-mt-24 border-t border-slate-100 pt-8"
                                    >
                                        <h2 className="mb-4 text-xl font-bold text-brand-dark">
                                            Leírás
                                        </h2>
                                        <AttachedBlocks blocks={event.cmsBlocks} className="mb-6" />
                                        {longDescriptionHtml && (
                                            <div
                                                className={longFormRichTextClassName}
                                                dangerouslySetInnerHTML={{
                                                    __html: longDescriptionHtml,
                                                }}
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="mt-10 flex flex-wrap gap-4 border-t border-slate-100 pt-8">
                                    {renderBookingButton()}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/events/$eventId')({
    component: EventDetailsPage,
    loader: async ({ context, params }) => {
        const [event, websiteSettings] = await Promise.allSettled([
            context.apiUtils.event.getById.ensureData({ id: params.eventId }),
            context.apiUtils.websiteSettings.getPublic.ensureData(),
            context.apiUtils.webContent.getAllPublic.ensureData(),
        ])
        return {
            event:
                event.status === 'fulfilled' && event.value ? withoutCmsBlocks(event.value) : null,
            websiteSettings: websiteSettings.status === 'fulfilled' ? websiteSettings.value : null,
            eventId: params.eventId,
        }
    },
    head: ({ loaderData }) => ({
        meta: buildOgMeta({
            title: loaderData?.event?.ogTitle || 'Long-Y - Esemény részletei',
            description: loaderData?.event?.ogDescription,
            imageId: loaderData?.event?.ogImageId,
            url: `${import.meta.env.VITE_WEB_URL || ''}/events/${loaderData?.eventId ?? ''}`,
            websiteSettings: loaderData?.websiteSettings,
        }),
    }),
})
